import { create } from 'zustand';

import { getView } from '../engine/prism.js';
import { setOverlay } from '../engine/host.js';
import { docIO, SAVE_DELAY_MS } from '../lib/doc-io.js';
import { applySceneCommand, parseScene, serializeScene, type EditorCommand, type SceneDoc } from '../lib/scene-doc.js';
import { storeHistorySource, undoService } from '../lib/undo.js';
import { logEditor } from './console.js';
import { useScene } from './scene.js';
import { useWorkspace } from './workspace.js';

/**
 * Multi-scene editing (Unity's additive scenes): the open scene in
 * {@link useScene} is the *active* one; other scenes can be added to the
 * Hierarchy next to it, loaded (shown in the Scene view and played together)
 * or unloaded (listed only), made active, edited and saved one by one.
 *
 * The engine draws loaded additive scenes under a host entity
 * `@scene:<path>` (`PrismView.setAdditiveScenes`). Prefab editing "in
 * context" uses the same mechanism for its read-only context scene.
 */

export interface AdditiveScene {
  path: string;
  /** Read when loaded (null until then, or when it failed). */
  doc: SceneDoc | null;
  loaded: boolean;
  error: string | null;
  saveState: 'saved' | 'pending' | 'saving' | 'error';
}

interface Snapshot {
  path: string;
  doc: SceneDoc;
}

interface SceneSetState {
  scenes: AdditiveScene[];
  /** Read-only context scenes (prefab editing in context). */
  context: string[];
  /** Entity of an additive scene selected in the Hierarchy. */
  selected: { path: string; id: string } | null;
  past: Snapshot[];
  future: Snapshot[];

  add: (path: string, loaded?: boolean) => Promise<void>;
  remove: (path: string) => Promise<void>;
  setLoaded: (path: string, loaded: boolean) => Promise<void>;
  /** Makes `path` the active scene (the current active one stays as an additive scene). */
  setActive: (path: string) => Promise<void>;
  /** Edits an additive scene (one undo step). */
  apply: (path: string, command: EditorCommand) => boolean;
  select: (target: { path: string; id: string } | null) => void;
  save: (path: string) => Promise<void>;
  saveAll: () => Promise<void>;
  setContext: (paths: string[]) => void;
  /** Sends the loaded scenes to the engine views. */
  syncEngine: () => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();

/** Paths the engine should draw next to the active scene. */
export function engineScenes(state: Pick<SceneSetState, 'scenes' | 'context'>, active: string | null): string[] {
  const paths = [...state.scenes.filter((s) => s.loaded && s.doc).map((s) => s.path), ...state.context];
  return [...new Set(paths)].filter((p) => p !== active);
}

export const useSceneSet = create<SceneSetState>((set, get) => {
  function replace(path: string, patch: Partial<AdditiveScene>): void {
    set((s) => ({ scenes: s.scenes.map((x) => (x.path === path ? { ...x, ...patch } : x)) }));
  }

  async function read(path: string): Promise<SceneDoc> {
    const pid = useWorkspace.getState().projectId;
    if (!pid) throw new Error('no project');
    const text = await docIO().read(pid, path);
    setOverlay(path, text);
    return parseScene(text);
  }

  function scheduleSave(path: string): void {
    replace(path, { saveState: 'pending' });
    const t = timers.get(path);
    if (t) clearTimeout(t);
    timers.set(
      path,
      setTimeout(() => {
        timers.delete(path);
        void get().save(path);
      }, SAVE_DELAY_MS),
    );
  }

  return {
    scenes: [],
    context: [],
    selected: null,
    past: [],
    future: [],

    add: async (path, loaded = true) => {
      if (path === useScene.getState().path || get().scenes.some((s) => s.path === path)) return;
      set((s) => ({ scenes: [...s.scenes, { path, doc: null, loaded, error: null, saveState: 'saved' }] }));
      if (loaded) await get().setLoaded(path, true);
    },

    remove: async (path) => {
      await get().save(path);
      set((s) => ({
        scenes: s.scenes.filter((x) => x.path !== path),
        selected: s.selected?.path === path ? null : s.selected,
        past: s.past.filter((p) => p.path !== path),
        future: s.future.filter((p) => p.path !== path),
      }));
      get().syncEngine();
    },

    setLoaded: async (path, loaded) => {
      const scene = get().scenes.find((s) => s.path === path);
      if (!scene) return;
      if (!loaded) await get().save(path);
      replace(path, { loaded });
      if (loaded && !scene.doc) {
        try {
          replace(path, { doc: await read(path), error: null });
        } catch (error) {
          replace(path, { error: error instanceof Error ? error.message : String(error), loaded: false });
        }
      }
      if (!loaded && get().selected?.path === path) set({ selected: null });
      get().syncEngine();
    },

    setActive: async (path) => {
      const scene = useScene.getState();
      const previous = scene.path;
      if (previous === path) return;
      await get().save(path);
      await scene.flush();
      set((s) => ({ scenes: s.scenes.filter((x) => x.path !== path), selected: null }));
      await useScene.getState().open(path);
      if (previous) await get().add(previous, true);
      get().syncEngine();
    },

    apply: (path, command) => {
      const scene = get().scenes.find((s) => s.path === path);
      if (!scene?.doc) return false;
      const result = applySceneCommand(scene.doc, command);
      if (!result.ok) {
        logEditor('warn', `${path}: ${result.error}`);
        return false;
      }
      if (result.doc === scene.doc) return true;
      set((s) => ({ past: [...s.past, { path, doc: scene.doc! }].slice(-100), future: [] }));
      replace(path, { doc: result.doc });
      setOverlay(path, serializeScene(result.doc));
      void getView('scene')?.fileChanged(path);
      scheduleSave(path);
      return true;
    },

    select: (target) => {
      set({ selected: target });
      if (target) useScene.getState().select([]);
    },

    save: async (path) => {
      const t = timers.get(path);
      if (t) {
        clearTimeout(t);
        timers.delete(path);
      }
      const scene = get().scenes.find((s) => s.path === path);
      const pid = useWorkspace.getState().projectId;
      if (!scene?.doc || !pid || scene.saveState === 'saved') return;
      replace(path, { saveState: 'saving' });
      try {
        await docIO().write(pid, path, serializeScene(scene.doc));
        replace(path, { saveState: 'saved' });
      } catch (error) {
        replace(path, { saveState: 'error' });
        logEditor('error', `Saving ${path} failed: ${error instanceof Error ? error.message : String(error)}`);
      }
    },

    saveAll: async () => {
      await useScene.getState().flush();
      for (const s of get().scenes) await get().save(s.path);
    },

    setContext: (paths) => {
      set({ context: paths });
      get().syncEngine();
    },

    syncEngine: () => {
      const paths = engineScenes(get(), useScene.getState().path);
      void getView('scene')?.setAdditiveScenes(paths);
    },

    undo: () => {
      const last = get().past.at(-1);
      const scene = last && get().scenes.find((s) => s.path === last.path);
      if (!last || !scene?.doc) return;
      set((s) => ({ past: s.past.slice(0, -1), future: [{ path: last.path, doc: scene.doc! }, ...s.future] }));
      replace(last.path, { doc: last.doc });
      setOverlay(last.path, serializeScene(last.doc));
      void getView('scene')?.fileChanged(last.path);
      scheduleSave(last.path);
    },

    redo: () => {
      const next = get().future[0];
      const scene = next && get().scenes.find((s) => s.path === next.path);
      if (!next || !scene?.doc) return;
      set((s) => ({ future: s.future.slice(1), past: [...s.past, { path: next.path, doc: scene.doc! }] }));
      replace(next.path, { doc: next.doc });
      setOverlay(next.path, serializeScene(next.doc));
      void getView('scene')?.fileChanged(next.path);
      scheduleSave(next.path);
    },

    reset: () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      set({ scenes: [], context: [], selected: null, past: [], future: [] });
    },
  };
});

undoService.register(
  storeHistorySource({ id: 'scene-additive', label: 'Additive scenes', view: 'scene', store: useSceneSet, stepLabel: (n) => `Additive scene edit ${n}` }),
);

// The active scene changed (opened from the Assets panel, prefab mode…):
// it can no longer be an additive one, and the engine re-reads the list.
useScene.subscribe((state, prev) => {
  // Selecting in the active scene ends the additive selection.
  if (state.selection !== prev.selection && state.selection.length > 0 && useSceneSet.getState().selected) {
    useSceneSet.setState({ selected: null });
  }
  if (state.path === prev.path) return;
  const set = useSceneSet.getState();
  if (state.path && set.scenes.some((s) => s.path === state.path)) {
    useSceneSet.setState({ scenes: set.scenes.filter((s) => s.path !== state.path) });
  }
  set.syncEngine();
});

// Another project: nothing of the old one stays loaded.
useWorkspace.subscribe((state, prev) => {
  if (state.projectId !== prev.projectId) useSceneSet.getState().reset();
});
